import { Button, Checkbox, Dialog, Div, Drawer, Flex, P, Section, Table, Td, Th, Tr } from '@dnb/eufemia';
import { trash as TrashIcon } from '@dnb/eufemia/icons';
import { hourglass as HourglassIcon, stop as StopIcon } from '@dnb/eufemia/icons';
import styled from '@emotion/styled';
import { Decision, type EntitlementRequestWithScopeDto } from '@portals/shared/portal/AppApiEntitlementDto';
import useAsync from '@portals/shared-frontend/hooks/useAsync';
import { type JSX, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { mutate } from 'swr';

import { acknowledgeDenial, cancelPendingRequest } from '@/api/app';
import Card from '@/components/Card';
import LoadingModal from '@/components/LoadingModal';
import { EntitlementRequestDetails } from '@/pages/profile/apps/application/components/EntitlementRequest/components/EntitlementRequestDetails';

import style from './index.module.css';

type EntitlementRequestProps = {
  entitlementRequests: EntitlementRequestWithScopeDto[];
  isAdmin: boolean;
};

interface RequestedScopes {
  scopes: Scope[];
  testScopes: Scope[];
}

type Scope = {
  id: string;
  description: string;
  name: string;
};

export function useGroupedScopes({ scopes, testScopes }: RequestedScopes) {
  return useMemo(() => {
    return [...scopes.map((s) => ({ ...s, env: 'Live' })), ...testScopes.map((s) => ({ ...s, env: 'Test' }))].reduce(
      (
        acc: Array<{
          id: string;
          name: string;
          description: string;
          envs: string[];
        }>,
        scope,
      ) => {
        const existing = acc.find((s) => s.name === scope.name);
        if (existing) {
          if (!existing.envs.includes(scope.env)) {
            existing.envs.push(scope.env);
          }
        } else {
          acc.push({
            id: scope.id,
            name: scope.name,
            description: scope.description,
            envs: [scope.env],
          });
        }
        return acc;
      },
      [],
    );
  }, [scopes, testScopes]);
}

function convertDecisionToText(status: Decision) {
  const Status = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
  `;
  switch (status) {
    case Decision.PRESUBMIT: {
      return (
        <Status>
          <HourglassIcon />
          <P left="x-small">Processing</P>
        </Status>
      );
    }
    case Decision.PENDING: {
      return (
        <Status>
          <HourglassIcon />
          <P left="x-small">Waiting for review</P>
        </Status>
      );
    }
    case Decision.DENIED: {
      return (
        <Status>
          <StopIcon />
          <P left="x-small">Your access is denied</P>
        </Status>
      );
    }
    case Decision.LEVEL1_APPROVED: {
      return (
        <Status>
          <StopIcon />
          <P left="x-small">Waiting for final approval</P>
        </Status>
      );
    }
    default: {
      return 'Unknown status';
    }
  }
}

export default function EntitlementRequest({ entitlementRequests, isAdmin }: EntitlementRequestProps): JSX.Element {
  const { id: appId } = useParams();

  return (
    <Card>
      <Table className={style['Table']}>
        <thead>
          <Tr>
            <Th>API</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </Tr>
        </thead>
        <tbody>
          {entitlementRequests.map((request) => (
            <Tr key={request.id}>
              <Td>{request.apiName}</Td>
              <Td>{convertDecisionToText(request.decision)}</Td>
              <Td>
                <Drawer key={request.id} title="Your request" triggerAttributes={{ text: 'View' }}>
                  <Drawer.Navigation />
                  <Drawer.Header>
                    <P bottom>The list below displays the scopes you have requested access for.</P>
                  </Drawer.Header>
                  <Drawer.Body>
                    {appId && <EntitlementRequestItem appId={appId} isAdmin={isAdmin} request={request} />}
                  </Drawer.Body>
                </Drawer>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function EntitlementRequestItem({
  request,
  isAdmin,
  appId,
}: {
  request: EntitlementRequestWithScopeDto;
  isAdmin: boolean;
  appId: string;
}): JSX.Element {
  const [userAck, setUserAck] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const onCancelRequest = useAsync(
    async (apiId: string, entitlementRequestId: string) => {
      if (appId) {
        await cancelPendingRequest(appId, apiId, entitlementRequestId);
        mutate(`/apps/${appId}`);
        setShowCancelDialog(false);
      }
    },
    [appId],
  );

  const onAcknowledgeDenial = useAsync(
    async (apiId: string, entitlementRequestId: string) => {
      if (appId) {
        await acknowledgeDenial(appId, apiId, entitlementRequestId);
        mutate(`/apps/${appId}`);
        setShowCancelDialog(false);
      }
    },
    [appId],
  );

  const groupedRequest = useGroupedScopes({
    scopes: request.scopes.map((scope) => ({ id: scope.id, name: scope.name, description: scope.description })),
    testScopes: request.testScopes.map((scope) => ({ id: scope.id, name: scope.name, description: scope.description })),
  });
  const requestProps = useMemo(
    () => ({
      scopes: request.scopes.map((scope) => scope.name),
      testScopes: request.testScopes.map((scope) => scope.name),
      externalTicketId: request.externalTicketId ?? null,
      createdAt: request.createdAt,
      decision: request.decision,
      decisionMadeAt: request.decisionMadeAt ?? null,
      decisionComment: request.decisionComment ?? null,
      externalTicketComment: request.externalTicketComment ?? null,
    }),
    [request],
  );
  return (
    <Section backgroundColor="transparent">
      {(onCancelRequest.waiting || onAcknowledgeDenial.waiting) && <LoadingModal />}
      <EntitlementRequestDetails
        apiName={request.apiName}
        groupedRequest={groupedRequest}
        requestProps={requestProps}
      />
      {isAdmin &&
        (request.decision == Decision.DENIED ? (
          <>
            <Div top="medium">
              <Checkbox
                label="I have read and understood why my request was rejected. "
                onChange={({ checked }) => setUserAck(checked)}
              />
            </Div>

            <Button
              disabled={!userAck}
              onClick={() => onAcknowledgeDenial.execute(request.apiId, request.id)}
              text="Confirm and close"
              top="small"
            />
          </>
        ) : (
          <Flex.Horizontal bottom="small" gap="small" top="small">
            <Button
              icon={TrashIcon}
              iconPosition="left"
              onClick={() => setShowCancelDialog(true)}
              text="Cancel Request"
              variant="primary"
            />
            <Dialog
              confirmText="Confirm"
              description={
                <>Canceling a request completely deletes it. You can make a new request whenever you like.</>
              }
              onConfirm={() => onCancelRequest.execute(request.apiId, request.id)}
              onDecline={() => setShowCancelDialog(false)}
              open={showCancelDialog}
              title="Are you sure you want to cancel the request?"
              triggerAttributes={{
                hidden: true,
              }}
              variant="confirmation"
            />
          </Flex.Horizontal>
        ))}
    </Section>
  );
}
